import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { computed } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { Kostenstelle } from '../../groups/entities/Kostenstelle.js';
import { School } from '../../groups/entities/School.js';
import { SGGroupCreationDialog } from '../../groups/list-page/sg-group-creation-dialog.js';
import { BillingPosition, BillingRhythm, translateBillingRhythm } from '../entities/BillingPosition.js';
import { BillingPositionPriceInput } from './BillingPositionPriceInput.js';
import { SGBillingPositionSelectionDialog } from './sg-billing-position-selection-dialog.js';

const BuchungskontoSelectionInput = stateful((props: { form: FunctionalForm<Group> }) => {
  const t = {
    '800011': '(800011) Schulgeld',
    '800012': '(800012) Betreuungskosten',
    '800911': '(800911) Elternengagement',
    '801010': '(801010) Sonstige Einnahmen',
    '801035': '(801035) Verpflegung',
    '801040': '(801040) Nachhilfe',
    '801043': '(801043) Vermietung, Instrumente',
    '860301': '(860301) Klassenfahrten',
    '801050': '(801050) Photovoltaik',
  };

  const [freeText, setFreeText] = useState(
    props.form.document.billingPosition?.buchungskonto
      ? !t[props.form.document.billingPosition.buchungskonto ?? '']
      : false,
  );

  return html`
    <div class="form-control sm:col-span-2">
      <label class="label py-1">
        <span class="label-text text-sm font-semibold text-base-content/70">Buchungskonto</span>
        <div class="flex items-center gap-1 tooltip scale-70 origin-left -my-1" data-tip="Freitext">
          <span class="icon-[material-symbols--edit-rounded] size-4 ${freeText ? '' : 'opacity-40'}"></span>
          ${ToggleInput({ value: freeText, onChanged: value => setFreeText(value ?? false) })}
        </div>
      </label>

      ${
        freeText
          ? TextInput({
              ...props.form.field('billingPosition', 'buchungskonto'),
              placeholder: 'Kein Buchungskonto hinterlegt',
              placeholderMode: 'hide',
            })
          : DropdownSelectionInput({
              ...props.form.field('billingPosition', 'buchungskonto'),
              placeholder: 'Kein Buchungskonto hinterlegt',

              renderString: value => {
                return t[value] ?? value;
              },
              placeholderMode: 'hide',
              selectables: ['800011', '800012', '800911', '801010', '801035', '801040', '801043', '860301', '801050'],
            })
      }
    </div>
  `;
});

/**
 * Edits the one Abrechnungsposition of a group. A booking that owes something else is a booking on
 * the group that bills it; a booking that pays a different price or rhythm carries an
 * override (`Booking.billingPosition`), edited in the member list.
 */
export const SGBillingPositionEdit = stateful((props: { form: FunctionalForm<Group> }) => {
  const billingPosition = props.form.document.billingPosition;

  const scopes = [props.form.document._id];

  const addBillingPosition = async () => {
    const template = await SGBillingPositionSelectionDialog({ scopes });
    if (!template) return;

    props.form.document.billingPosition = new BillingPosition({
      ...template.toJSON(),
      templateID: template._id ?? null,
    });
  };

  const removeBillingPosition = async () => {
    const confirmed = await confirmDialog(
      'Möchten Sie die Abrechnungsposition wirklich entfernen? Die Gruppe wird dann nicht mehr abgerechnet. Diese Änderung muss gespeichert werden!',
      {
        highlightDecline: true,
      },
    );
    if (!confirmed) return;
    props.form.document.billingPosition = null;
  };

  const connectedSchoolIds = computed(() => {
    const schoolIds: string[] = [];

    /**
     * Parent is a school itself -> use itself
     */
    if (props.form.document instanceof School) {
      schoolIds.push(props.form.document._id);
      return schoolIds;
    }

    /**
     * The group is a school or a group which might have a school in its parents
     */
    const getSchoolFromGroup = (group: Group): string | null => {
      if (group instanceof School) {
        return group._id;
      }

      const parents = group.parents?.filter(isNotNullish) ?? [];
      if (!group.parentIDs?.length) return null;
      for (const parent of parents) {
        if (parent instanceof School) {
          return parent._id;
        }
        const schoolId = getSchoolFromGroup(parent);
        if (schoolId) return schoolId;
      }

      return null;
    };

    const schoolId = getSchoolFromGroup(props.form.document);
    if (schoolId) schoolIds.push(schoolId);

    return schoolIds;
  });

  if (!billingPosition) {
    return html`
      <div class="flex flex-col gap-4">
        <div class="text-center p-4">
          <p class="text-base-content/70">Keine Abrechnungsposition vorhanden - die Gruppe wird nicht abgerechnet.</p>
        </div>
        <button type="button" class="btn btn-dash btn-block justify-start" @click=${addBillingPosition}>
          <span class="icon-[material-symbols--add-rounded] size-8 text-success"></span>
          <span>Abrechnungsposition hinzufügen</span>
        </button>
      </div>
    `;
  }

  return html`
    <div class="card">
      <div class="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        <h3 class="truncate font-semibold">
          ${billingPosition.name || html`<span class="text-base-content/70 italic">Unbenannt</span>`}
        </h3>
        <button class="btn btn-error btn-outline btn-sm" @click=${removeBillingPosition}>
          <span class="icon-[material-symbols--delete-rounded] size-4"></span>
          Löschen
        </button>
      </div>

      <div class="px-4 pb-4">
        <div class="grid grid-cols-1 sm:grid-cols-15 gap-2">
          <div class="form-control sm:col-span-1">
            <label class="label py-1">
              <span class="label-text text-sm font-semibold text-base-content/70">Kürzel</span>
            </label>
            ${TextInput({
              ...props.form.field('billingPosition', 'kuerzel'),
              disabled: !!billingPosition.templateID,
              clearable: true,
              placeholder: 'z.B. MB',
              placeholderMode: 'hide',
            })}
          </div>

          <div class="form-control sm:col-span-2">
            <label class="label py-1">
              <span class="label-text text-sm font-semibold text-base-content/70">Name</span>
            </label>
            ${TextInput({
              ...props.form.field('billingPosition', 'name'),
              disabled: !!billingPosition.templateID,
              clearable: true,
              placeholder: 'z.B. Mitgliedsbeitrag',
              placeholderMode: 'hide',
            })}
          </div>

          <div class="form-control sm:col-span-2">
            <label class="label py-1">
              <span class="label-text text-sm font-semibold text-base-content/70">Betrag</span>
            </label>
            ${BillingPositionPriceInput(props.form.field('billingPosition', 'priceInCents'))}
          </div>

          <div class="form-control sm:col-span-4">
            <label class="label py-1">
              <span class="label-text text-sm font-semibold text-base-content/70">Rhythmus</span>
            </label>
            ${DropdownSelectionInput({
              ...props.form.field('billingPosition', 'rhythm'),
              disabled: !!props.form.document.billingRhythmFixed,
              placeholder: '(Leer für Selbstauswahl)',
              placeholderMode: 'hide',
              selectables: Object.values(BillingRhythm),
              renderString: rhythm => translateBillingRhythm(rhythm),
            })}
            ${
              props.form.document.billingRhythmFixed
                ? html`<span class="text-sm text-base-content/70">
                    Nach der ersten Abrechnung fest. Für einen anderen Rhythmus eine neue Gruppe anlegen.
                  </span>`
                : nothing
            }
          </div>

          <div class="form-control sm:col-span-4">
            <label class="label py-1">
              <span class="label-text text-sm font-semibold text-base-content/70">Kostenstelle</span>
            </label>
            <div
              class="flex items-stretch [&>div]:flex-1 [&>div>div:first-child]:rounded-r-none [&>div>div:first-child]:border-r-0"
            >
              ${EntityPickerInput({
                ...props.form.field('billingPosition', 'kostenstelleID'),
                clearable: true,
                // TODO: use own subscribtion for search to actually filter the prefix and suffix
                placeholder: 'Keine Kostenstelle ausgewählt',
                placeholderMode: 'hide',
                class: Kostenstelle,
                operation: GroupsAPI.subscribeGroups,
                renderString: k =>
                  `${(k.parents?.at(0)?.kostenstellePrefix as Maybe<string>) ?? ''}${k.kostenstelleSuffix} ${k.name}`,
                filter: new AdornisFilter({
                  searchFields: ['name', 'kostenstelleSuffix'],
                  aspects: [
                    new StringAspect({
                      fieldName: '_class',
                      value: Kostenstelle._class,
                      mode: 'equals',
                    }),
                    new ListAspect({
                      fieldName: 'parentIDs',
                      values: connectedSchoolIds.value,
                    }),
                  ],
                }),
                selectionSet: {
                  _id: 1,
                  name: 1,
                  kostenstelleSuffix: 1,
                  parentIDs: 1,
                  parents: {
                    kostenstellePrefix: 1,
                  },
                },
              })}
              <button
                type="button"
                tabindex="-1"
                class="btn btn-square rounded-l-none border border-base-300 bg-base-200 hover:bg-success/50 self-stretch h-auto"
                aria-label="Neue Kostenstelle anlegen"
                data-tip="Neue Kostenstelle anlegen"
                ${hoverTooltip()}
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  const newID = await SGGroupCreationDialog(true, {
                    pickedClass: Kostenstelle._class,
                    pickedSchoolIDs: connectedSchoolIds.value,
                  });
                  const position = props.form.document.billingPosition;
                  if (newID && position) {
                    // TODO reactivity in the entity picker is broken and needs to be fixed in order for this to work
                    position.kostenstelleID = newID;
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="12" y1="4" x2="12" y2="20"></line>
                  <line x1="4" y1="12" x2="20" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>

          ${BuchungskontoSelectionInput({ form: props.form })}
        </div>
      </div>
    </div>
  `;
});
